import { useEffect, useState, useMemo } from "react";
import { api, inr } from "../../lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, MapPin, Package as PackageIcon, ClipboardList, Pencil, X, Truck, FileArchive, Send, FileDown, FileSpreadsheet, Wallet, CheckCircle2 } from "lucide-react";
import { COURIERS, DEFAULT_COURIER_KEY, courierName } from "../../lib/couriers";

const LEDGER_STATUSES = ["open", "dispatched", "delivered", "cancelled"];
const LEDGER_COLOR = {
  open:       { bg: "#FEF3E2", text: "#8B7355", label: "Open" },
  dispatched: { bg: "#DDE7F0", text: "#1F3F5F", label: "Dispatched" },
  delivered:  { bg: "#E8EDDF", text: "#2E4A35", label: "Delivered" },
  cancelled:  { bg: "#F5DEC7", text: "#993322", label: "Cancelled" },
};
const SOURCE_COLOR = {
  online:    { bg: "#3B2F2F", text: "#FDFBF7", label: "Online" },
  offline:   { bg: "#D4AF37", text: "#3B2F2F", label: "Offline" },
  wholesale: { bg: "#2E4A35", text: "#FDFBF7", label: "Seller" },
  reseller:  { bg: "#8B5E3C", text: "#FDFBF7", label: "Reseller" },
};
const sourceStyle = (src) => SOURCE_COLOR[src] || { bg: "#8B7355", text: "#FDFBF7", label: (src || "Other").toString() };
const ledgerStyle = (ls)  => LEDGER_COLOR[ls] || { bg: "#F5F5F5", text: "#3B2F2F", label: (ls || "—").toString() };

const emptyLine = () => ({ product_name: "", variant_label: "", qty: 1, unit_price: 0 });
const emptyForm = () => ({
  customer_name: "", email: "", phone: "",
  address: { line1: "", city: "", state: "", pincode: "", country: "India" },
  items: [emptyLine()],
  discount: 0, shipping: 0, tax: 0,
  payment_method: "cash", payment_status: "paid", notes: "",
  awb_number: "",
  courier: DEFAULT_COURIER_KEY,
  buyer_gstin: "", buyer_company_name: "",
});
const orderToForm = (o) => ({
  customer_name: o.customer_name || "",
  email: o.email || "",
  phone: o.phone || "",
  address: {
    line1: o.address?.line1 || "", city: o.address?.city || "",
    state: o.address?.state || "", pincode: o.address?.pincode || "",
    country: o.address?.country || "India",
  },
  items: (o.items || []).map(it => ({
    product_id: it.product_id, variant_sku: it.variant_sku, image: it.image,
    product_name: it.product_name || "", variant_label: it.variant_label || "",
    qty: it.qty || 1, unit_price: it.unit_price || 0,
    cost_price: it.cost_price,
  })),
  discount: o.discount || 0, shipping: o.shipping || 0, tax: o.tax || 0,
  payment_method: o.payment_method || "cash",
  payment_status: o.payment_status || "paid",
  notes: o.notes || "",
  awb_number: o.awb_number || "",
  courier: o.courier || DEFAULT_COURIER_KEY,
  buyer_gstin: o.buyer_gstin || "",
  buyer_company_name: o.buyer_company_name || "",
});

export default function AdminLedger() {
  const [orders, setOrders] = useState([]);
  const [ledgerFilter, setLedgerFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [mode, setMode] = useState(null); // "add" | "edit" | null
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [settleFor, setSettleFor] = useState(null);
  const [settleForm, setSettleForm] = useState({ total: 0, reseller_payout: 0, settlement_status: "pending", note: "" });
  const [settling, setSettling] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const todayIso = () => new Date().toISOString().slice(0, 10);
  const monthAgoIso = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); };
  const [exportForm, setExportForm] = useState({ preset: "this-month", start: monthAgoIso(), end: todayIso() });

  const load = () => {
    const params = new URLSearchParams();
    if (ledgerFilter !== "all") params.append("ledger_status", ledgerFilter);
    if (sourceFilter !== "all") params.append("source", sourceFilter);
    const q = params.toString() ? `?${params}` : "";
    api.get(`/admin/orders${q}`).then(r => { setOrders(r.data); setSelected(new Set()); }).catch(()=>{});
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ledgerFilter, sourceFilter]);

  const openSettlement = (o) => {
    setSettleFor(o);
    setSettleForm({
      total: Number(o.total || 0),
      reseller_payout: Number(o.reseller_payout || 0),
      settlement_status: o.settlement_status || "pending",
      note: "",
    });
  };
  const saveSettlement = async () => {
    setSettling(true);
    try {
      await api.post(`/admin/orders/${settleFor.order_id}/settlement`, settleForm);
      toast.success("Settlement updated");
      setSettleFor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSettling(false); }
  };

  const downloadStatement = async () => {
    setExporting(true);
    const now = new Date();
    const params = new URLSearchParams();
    let filenameLabel = "custom";
    if (exportForm.preset === "this-month") {
      const m = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      params.append("month", m); filenameLabel = m;
    } else if (exportForm.preset === "last-month") {
      const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      params.append("month", m); filenameLabel = m;
    } else if (exportForm.preset === "all-time") {
      filenameLabel = "all-time";
    } else {
      if (!exportForm.start || !exportForm.end) {
        toast.error("Pick both start and end dates"); setExporting(false); return;
      }
      if (exportForm.start > exportForm.end) {
        toast.error("Start date must be before end date"); setExporting(false); return;
      }
      params.append("start", exportForm.start);
      params.append("end", exportForm.end);
      filenameLabel = `${exportForm.start}_to_${exportForm.end}`;
    }
    if (sourceFilter !== "all") params.append("source", sourceFilter);
    if (ledgerFilter !== "all") params.append("ledger_status", ledgerFilter);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/export.xlsx?${params}`;
    try {
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t.includes("No orders") ? `No orders found for this period` : `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `DailyBeans-Orders-${filenameLabel}.xlsx`;
      a.click();
      toast.success("Excel statement downloaded");
      setExportOpen(false);
    } catch (e) {
      toast.error(e.message || "Failed to export");
    } finally { setExporting(false); }
  };

  const downloadMonthInvoices = () => {
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/invoices/bulk?month=${month}`;
    // Use fetch so we can send credentials and read the blob (browsers block cross-origin
    // downloads via naked <a href> when the response has custom headers)
    fetch(url, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) {
          const t = await r.text();
          throw new Error(t.includes("No orders") ? `No orders in ${month}` : `HTTP ${r.status}`);
        }
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `DailyBean-Invoices-${month}.zip`;
        a.click();
      })
      .catch((e) => toast.error(e.message || "Failed to download invoices"));
  };

  const kpis = useMemo(() => {
    const c = { total: orders.length, open: 0, dispatched: 0, delivered: 0, cancelled: 0, online: 0, offline: 0, revenue: 0 };
    orders.forEach(o => {
      const ls = o.ledger_status || "open";
      c[ls] = (c[ls] || 0) + 1;
      c[o.source || "online"] = (c[o.source || "online"] || 0) + 1;
      if (ls !== "cancelled") c.revenue += (o.total || 0);
    });
    return c;
  }, [orders]);

  const setLedger = async (order_id, value) => {
    try {
      await api.post(`/admin/orders/${order_id}/ledger-status`, { ledger_status: value });
      toast.success(`Marked as ${value}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const saveAwb = async (order_id, order_no, value, courierKey) => {
    try {
      await api.post(`/admin/orders/${order_id}/awb`, { awb_number: value, courier: courierKey || DEFAULT_COURIER_KEY });
      toast.success(value ? `AWB saved for ${order_no} via ${courierName(courierKey)}` : `AWB cleared for ${order_no}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save AWB"); }
  };

  const resendConfirmation = async (order_id, order_no, phone) => {
    if (!phone) { toast.error("This order has no phone number"); return; }
    if (!window.confirm(`Send Order Confirmation SMS to ${phone} for ${order_no}?`)) return;
    try {
      const r = await api.post(`/admin/orders/${order_id}/resend-confirmation`);
      toast.success(`Confirmation SMS sent · ${r.data.sms_status}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send confirmation"); }
  };

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === orders.length) setSelected(new Set());
    else setSelected(new Set(orders.map(o => o.order_id)));
  };

  const doBulkDelete = async () => {
    try {
      const r = await api.post("/admin/orders/bulk-delete", { order_ids: Array.from(selected) });
      toast.success(`${r.data.deleted} order${r.data.deleted!==1?"s":""} deleted`);
      setConfirmBulk(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const doSingleDelete = async (order_id, order_no) => {
    if (!window.confirm(`Delete order ${order_no}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${order_id}`);
      toast.success(`Order ${order_no} deleted`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const openEdit = (o) => { setEditing(o); setForm(orderToForm(o)); setMode("edit"); };
  const openAdd  = () => { setEditing(null); setForm(emptyForm()); setMode("add"); };
  const closeModal = () => { setMode(null); setEditing(null); setForm(emptyForm()); };

  const updLine = (i, k, v) => setForm(f => ({ ...f, items: f.items.map((x, idx) => idx===i ? {...x, [k]: v} : x) }));
  const addLine = () => setForm(f => ({ ...f, items: [...f.items, emptyLine()] }));
  const rmLine = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const subtotal = form.items.reduce((s, x) => s + (Number(x.qty)||0) * (Number(x.unit_price)||0), 0);
  const total = subtotal - (Number(form.discount)||0) + (Number(form.shipping)||0) + (Number(form.tax)||0);

  const submitForm = async () => {
    if (form.items.some(x => !x.product_name || !x.qty || x.unit_price < 0)) {
      toast.error("Fill every item name, qty and price"); return;
    }
    setSaving(true);
    try {
      if (mode === "add") {
        await api.post("/admin/offline-orders", form);
        toast.success("Offline order added to ledger");
      } else {
        await api.patch(`/admin/orders/${editing.order_id}`, form);
        toast.success(`Order ${editing.order_no} updated`);
      }
      closeModal(); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const allSelected = orders.length > 0 && selected.size === orders.length;
  const anySelected = selected.size > 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-outfit font-bold flex items-center gap-2">
            <ClipboardList className="w-7 h-7 text-[#D4AF37]"/> Order Ledger
          </h1>
          <div className="text-sm text-stone-500">All orders — online + offline, together in one book.</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={()=>setExportOpen(true)} data-testid="ledger-export-excel"
                  className="bg-white border border-[#2E4A35]/40 text-[#2E4A35] rounded-full px-4 py-2.5 text-sm font-medium hover:bg-[#E8EDDF] flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4"/> Export to Excel
          </button>
          <button onClick={downloadMonthInvoices} data-testid="ledger-bulk-invoices"
                  className="bg-white border border-stone-300 text-[#3B2F2F] rounded-full px-4 py-2.5 text-sm font-medium hover:bg-stone-50 flex items-center gap-2">
            <FileArchive className="w-4 h-4"/> Download this month's invoices
          </button>
          <button onClick={openAdd} data-testid="ledger-add-offline"
                  className="bg-[#3B2F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-black flex items-center gap-2">
            <Plus className="w-4 h-4"/> Add offline order
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <Kpi label="Total" value={kpis.total} accent="#3B2F2F"/>
        <Kpi label="Open" value={kpis.open} accent="#8B7355"/>
        <Kpi label="Dispatched" value={kpis.dispatched} accent="#1F3F5F"/>
        <Kpi label="Delivered" value={kpis.delivered} accent="#2E4A35"/>
        <Kpi label="Cancelled" value={kpis.cancelled} accent="#993322"/>
        <Kpi label="Revenue (excl. cancelled)" value={inr(kpis.revenue)} accent="#D4AF37"/>
      </div>

      <div className="flex flex-wrap gap-3 mb-4 bg-white border border-stone-200 rounded-xl p-3 items-center">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-stone-500">Status</span>
          <Select value={ledgerFilter} onValueChange={setLedgerFilter}>
            <SelectTrigger className="w-36" data-testid="ledger-status-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {LEDGER_STATUSES.map(s => <SelectItem key={s} value={s}>{LEDGER_COLOR[s].label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-stone-500">Source</span>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-36" data-testid="ledger-source-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="online">Online ({kpis.online})</SelectItem>
              <SelectItem value="offline">Offline ({kpis.offline})</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {anySelected && (
          <div className="ml-auto flex items-center gap-3 bg-[#F5DEC7]/40 border border-[#993322]/30 rounded-full pl-4 pr-2 py-1.5">
            <span className="text-xs font-medium text-[#993322]" data-testid="ledger-selected-count">{selected.size} selected</span>
            <button onClick={()=>setSelected(new Set())} className="text-stone-500 hover:text-stone-800" data-testid="ledger-clear-selection"><X className="w-3.5 h-3.5"/></button>
            <button onClick={()=>setConfirmBulk(true)} data-testid="ledger-bulk-delete"
                    className="bg-[#993322] text-white text-xs rounded-full px-3 py-1.5 flex items-center gap-1 hover:bg-[#7a2a1c]">
              <Trash2 className="w-3.5 h-3.5"/> Delete
            </button>
          </div>
        )}
      </div>

      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <input type="checkbox" checked={allSelected} onChange={toggleAll}
                       data-testid="ledger-select-all"
                       className="w-4 h-4 accent-[#3B2F2F] cursor-pointer"/>
              </TableHead>
              <TableHead>Order #</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Items & Weight</TableHead>
              <TableHead>City</TableHead>
              <TableHead>Total</TableHead>
              <TableHead className="w-48">Ledger status</TableHead>
              <TableHead className="w-44">AWB / Tracking</TableHead>
              <TableHead className="w-32 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 && (
              <TableRow><TableCell colSpan={11} className="text-center text-stone-400 py-10">No orders yet. Try changing filters or add an offline order.</TableCell></TableRow>
            )}
            {orders.map(o => {
              const ls = o.ledger_status || "open";
              const src = o.source || "online";
              const checked = selected.has(o.order_id);
              return (
                <TableRow key={o.order_id} className={`hover:bg-stone-50 ${checked ? "bg-[#FEF3E2]/40" : ""}`} data-testid={`ledger-row-${o.order_no}`}>
                  <TableCell>
                    <input type="checkbox" checked={checked} onChange={()=>toggle(o.order_id)}
                           data-testid={`ledger-select-${o.order_no}`}
                           className="w-4 h-4 accent-[#3B2F2F] cursor-pointer"/>
                  </TableCell>
                  <TableCell className="font-medium cursor-pointer" onClick={()=>setDetail(o)}>{o.order_no}</TableCell>
                  <TableCell><Badge style={{background: sourceStyle(src).bg, color: sourceStyle(src).text}}>{sourceStyle(src).label}</Badge></TableCell>
                  <TableCell className="text-xs">{new Date(o.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-xs">
                    <div className="font-medium">{o.customer_name || o.email || "—"}</div>
                    <div className="text-stone-400">{o.phone}</div>
                  </TableCell>
                  <TableCell className="text-xs max-w-xs">
                    {o.items.map((i, idx) => (
                      <div key={idx} className="truncate">
                        {i.qty}× {i.product_name}
                        {i.variant_label ? <span className="text-[#8B7355]"> · {i.variant_label}</span> : null}
                      </div>
                    ))}
                  </TableCell>
                  <TableCell className="text-xs">{o.address?.city || "—"}</TableCell>
                  <TableCell className="font-outfit font-semibold">{inr(o.total)}</TableCell>
                  <TableCell>
                    <Select value={ls} onValueChange={(v)=>setLedger(o.order_id, v)}>
                      <SelectTrigger className="h-8 text-xs" style={{background: ledgerStyle(ls).bg, color: ledgerStyle(ls).text, borderColor: ledgerStyle(ls).text+"40"}} data-testid={`ledger-status-${o.order_no}`}>
                        <SelectValue/>
                      </SelectTrigger>
                      <SelectContent>
                        {LEDGER_STATUSES.map(s => <SelectItem key={s} value={s}>{LEDGER_COLOR[s].label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <AwbCell order={o} onSave={saveAwb}/>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.order_id}/invoice.pdf`}
                       target="_blank" rel="noreferrer"
                       data-testid={`ledger-invoice-${o.order_no}`}
                       title="Download GST invoice PDF"
                       className="text-[#D4AF37] hover:bg-[#FEF3E2] rounded-md p-1.5 inline-flex">
                      <FileDown className="w-3.5 h-3.5"/>
                    </a>
                    <button onClick={()=>resendConfirmation(o.order_id, o.order_no, o.phone)}
                            data-testid={`ledger-resend-confirmation-${o.order_no}`}
                            disabled={!o.phone}
                            title={o.phone ? "Send Order Confirmation SMS" : "No phone on this order"}
                            className="text-[#2E4A35] hover:bg-[#E8EDDF] rounded-md p-1.5 inline-flex ml-1 disabled:opacity-30 disabled:hover:bg-transparent">
                      <Send className="w-3.5 h-3.5"/>
                    </button>
                    <button onClick={()=>openEdit(o)} data-testid={`ledger-edit-${o.order_no}`}
                            className="text-[#3B2F2F] hover:bg-stone-100 rounded-md p-1.5 inline-flex ml-1" title="Edit">
                      <Pencil className="w-3.5 h-3.5"/>
                    </button>
                    {o.source === "reseller" && (
                      <button onClick={()=>openSettlement(o)} data-testid={`ledger-settle-${o.order_no}`}
                              className="text-[#8B5E3C] hover:bg-[#FEF3E2] rounded-md p-1.5 inline-flex ml-1"
                              title={`Settle · payout ${(o.settlement_status==='paid') ? 'paid' : 'pending'}`}>
                        <Wallet className="w-3.5 h-3.5"/>
                      </button>
                    )}
                    <button onClick={()=>doSingleDelete(o.order_id, o.order_no)} data-testid={`ledger-delete-${o.order_no}`}
                            className="text-[#993322] hover:bg-red-50 rounded-md p-1.5 inline-flex ml-1" title="Delete">
                      <Trash2 className="w-3.5 h-3.5"/>
                    </button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Bulk-delete confirm */}
      <Dialog open={confirmBulk} onOpenChange={setConfirmBulk}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Delete {selected.size} order{selected.size!==1?"s":""}?</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-600">This permanently removes the selected orders from your ledger. This cannot be undone.</p>
          <div className="flex justify-end gap-2 pt-3">
            <button onClick={()=>setConfirmBulk(false)} className="border border-stone-300 rounded-full px-5 py-2 text-sm hover:bg-stone-50">Cancel</button>
            <button onClick={doBulkDelete} data-testid="ledger-bulk-delete-confirm"
                    className="bg-[#993322] text-white rounded-full px-5 py-2 text-sm font-medium hover:bg-[#7a2a1c]">
              Yes, delete
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reseller settlement modal */}
      <Dialog open={!!settleFor} onOpenChange={v=>!v && setSettleFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#8B5E3C]"/> Settle {settleFor?.order_no}
          </DialogTitle></DialogHeader>
          {settleFor && (
            <div className="space-y-4">
              <div className="bg-[#FEF3E2]/50 border border-[#D4AF37]/30 rounded-lg p-3 text-xs">
                <div className="flex justify-between"><span className="text-stone-500">Customer</span><span className="font-medium">{settleFor.customer_name}</span></div>
                <div className="flex justify-between"><span className="text-stone-500">Reseller</span><span className="font-medium">{settleFor.reseller_business_name || "—"}</span></div>
                <div className="flex justify-between"><span className="text-stone-500">Original total</span><span className="font-outfit font-semibold">{inr(settleFor.subtotal || settleFor.total)}</span></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500">Final total paid by customer *</label>
                  <Input type="number" min="0" step="0.01" value={settleForm.total}
                          onChange={e=>setSettleForm(f=>({...f, total: Number(e.target.value)}))}
                          data-testid="settle-total"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500">Reseller payout</label>
                  <Input type="number" min="0" step="0.01" value={settleForm.reseller_payout}
                          onChange={e=>setSettleForm(f=>({...f, reseller_payout: Number(e.target.value)}))}
                          data-testid="settle-payout"/>
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-widest text-stone-500">Status</label>
                  <Select value={settleForm.settlement_status} onValueChange={v=>setSettleForm(f=>({...f, settlement_status: v}))}>
                    <SelectTrigger data-testid="settle-status"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending payout</SelectItem>
                      <SelectItem value="paid">Paid to reseller</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Input className="col-span-2" placeholder="Note (optional)"
                        value={settleForm.note}
                        onChange={e=>setSettleForm(f=>({...f, note: e.target.value}))}/>
              </div>
              {settleForm.total > 0 && settleForm.reseller_payout > 0 && (
                <div className="text-xs text-stone-500 bg-stone-50 rounded-lg px-3 py-2">
                  Margin kept by DailyBeans: <span className="font-outfit font-semibold text-[#2E4A35]">{inr(settleForm.total - settleForm.reseller_payout)}</span>
                  <span className="text-stone-400"> ({((settleForm.total - settleForm.reseller_payout) / settleForm.total * 100).toFixed(1)}%)</span>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <button onClick={()=>setSettleFor(null)} className="border border-stone-300 rounded-full px-5 py-2 text-sm hover:bg-stone-50">Cancel</button>
                <button onClick={saveSettlement} disabled={settling} data-testid="settle-save"
                        className="bg-[#8B5E3C] text-white rounded-full px-6 py-2 text-sm font-medium hover:bg-[#6b4a2e] disabled:opacity-60 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4"/>
                  {settling ? "Saving…" : "Save settlement"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Export to Excel */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-[#2E4A35]"/> Export orders to Excel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-stone-600">
              Download a bank-statement style spreadsheet of orders for the period you pick.
              Current filters (source/status) will be applied to the export.
            </p>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Period</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: "this-month", label: "This month" },
                  { key: "last-month", label: "Last month" },
                  { key: "last-30",    label: "Last 30 days" },
                  { key: "custom",     label: "Custom range" },
                  { key: "all-time",   label: "All time" },
                ].map(opt => (
                  <button key={opt.key}
                    data-testid={`export-preset-${opt.key}`}
                    onClick={()=>{
                      const now = new Date();
                      if (opt.key === "last-30") {
                        const d = new Date(); d.setDate(d.getDate() - 30);
                        setExportForm({ preset: "custom", start: d.toISOString().slice(0,10), end: todayIso() });
                      } else if (opt.key === "custom") {
                        setExportForm(f => ({ ...f, preset: "custom" }));
                      } else if (opt.key === "this-month") {
                        const s = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0,10);
                        setExportForm({ preset: "this-month", start: s, end: todayIso() });
                      } else if (opt.key === "last-month") {
                        const s = new Date(now.getFullYear(), now.getMonth()-1, 1);
                        const e = new Date(now.getFullYear(), now.getMonth(), 0);
                        setExportForm({ preset: "last-month", start: s.toISOString().slice(0,10), end: e.toISOString().slice(0,10) });
                      } else {
                        setExportForm({ preset: "all-time", start: "", end: "" });
                      }
                    }}
                    className={`text-sm px-3 py-2 rounded-lg border transition ${
                      exportForm.preset === opt.key
                        ? "bg-[#3B2F2F] text-white border-[#3B2F2F]"
                        : "bg-white border-stone-200 hover:border-[#D4AF37] text-stone-700"
                    }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            {exportForm.preset === "custom" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500">From</label>
                  <Input type="date" value={exportForm.start}
                          onChange={e=>setExportForm(f=>({...f, start: e.target.value}))}
                          data-testid="export-start-date"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500">To</label>
                  <Input type="date" value={exportForm.end}
                          onChange={e=>setExportForm(f=>({...f, end: e.target.value}))}
                          data-testid="export-end-date"/>
                </div>
              </div>
            )}
            {(sourceFilter !== "all" || ledgerFilter !== "all") && (
              <div className="bg-[#FEF3E2]/50 border border-[#D4AF37]/30 rounded-lg p-3 text-xs text-stone-700">
                <div className="font-semibold text-[#8B7355] uppercase tracking-wide mb-1">Active filters</div>
                {sourceFilter !== "all" && <div>Source: <span className="font-medium">{sourceFilter}</span></div>}
                {ledgerFilter !== "all" && <div>Ledger status: <span className="font-medium">{ledgerFilter}</span></div>}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <button onClick={()=>setExportOpen(false)}
                      className="border border-stone-300 rounded-full px-5 py-2 text-sm hover:bg-stone-50">Cancel</button>
              <button onClick={downloadStatement} disabled={exporting}
                      data-testid="export-download-btn"
                      className="bg-[#2E4A35] text-white rounded-full px-6 py-2 text-sm font-medium hover:bg-[#25392a] disabled:opacity-60 flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4"/>
                {exporting ? "Preparing…" : "Download Excel"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={(v)=>!v && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          {detail && (<>
            <DialogHeader><DialogTitle>Order {detail.order_no}</DialogTitle></DialogHeader>
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Info label="Customer">{detail.customer_name || detail.email || "—"}</Info>
                <Info label="Phone">{detail.phone || "—"}</Info>
                <Info label="Source"><Badge style={{background: sourceStyle(detail.source||"online").bg, color: sourceStyle(detail.source||"online").text}}>{sourceStyle(detail.source||"online").label}</Badge></Info>
                <Info label="Payment">{detail.payment_method} · <span className="text-stone-500">{detail.payment_status}</span></Info>
              </div>
              <div className="flex gap-2 items-start bg-stone-50 rounded-lg p-3">
                <MapPin className="w-4 h-4 text-[#8B7355] shrink-0 mt-0.5"/>
                <div className="text-xs">
                  {detail.address?.line1 || "—"}<br/>
                  {detail.address?.city} {detail.address?.state} {detail.address?.pincode}<br/>
                  {detail.address?.country}
                </div>
              </div>
              <div className="border-t pt-3">
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Items</div>
                <div className="space-y-1.5">
                  {detail.items.map((i,k)=>(
                    <div key={k} className="flex justify-between text-sm">
                      <span>{i.qty}× {i.product_name} {i.variant_label && <span className="text-[#8B7355]">· {i.variant_label}</span>}</span>
                      <span className="font-outfit">{inr(i.line_total)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-3 pt-3 border-t font-outfit font-bold text-base">
                  <span>Total</span><span>{inr(detail.total)}</span>
                </div>
              </div>
              {detail.notes && (
                <div className="border-t pt-3">
                  <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Notes</div>
                  <div className="text-sm text-stone-700">{detail.notes}</div>
                </div>
              )}
              <div className="flex gap-2 justify-end pt-3 border-t">
                <button onClick={()=>{ setDetail(null); openEdit(detail); }}
                        className="bg-[#3B2F2F] text-white rounded-full px-5 py-2 text-sm font-medium hover:bg-black flex items-center gap-1.5">
                  <Pencil className="w-3.5 h-3.5"/> Edit order
                </button>
              </div>
            </div>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Add / Edit modal */}
      <Dialog open={!!mode} onOpenChange={(v)=>!v && closeModal()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {mode === "edit" ? <><Pencil className="w-5 h-5"/> Edit order {editing?.order_no}</>
                                : <><PackageIcon className="w-5 h-5"/> Add offline order</>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <section>
              <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Customer</div>
              <div className="grid grid-cols-3 gap-3">
                <Input placeholder="Customer name" value={form.customer_name} onChange={e=>setForm(f=>({...f, customer_name: e.target.value}))} data-testid="off-cust-name"/>
                <Input placeholder="Phone" value={form.phone} onChange={e=>setForm(f=>({...f, phone: e.target.value}))} data-testid="off-cust-phone"/>
                <Input placeholder="Email (optional)" value={form.email} onChange={e=>setForm(f=>({...f, email: e.target.value}))} data-testid="off-cust-email"/>
              </div>
            </section>
            <section>
              <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Address (optional)</div>
              <div className="grid grid-cols-2 gap-3">
                <Input className="col-span-2" placeholder="Street address" value={form.address.line1} onChange={e=>setForm(f=>({...f, address:{...f.address, line1: e.target.value}}))} data-testid="off-addr-line1"/>
                <Input placeholder="City" value={form.address.city} onChange={e=>setForm(f=>({...f, address:{...f.address, city: e.target.value}}))} data-testid="off-addr-city"/>
                <Input placeholder="State" value={form.address.state} onChange={e=>setForm(f=>({...f, address:{...f.address, state: e.target.value}}))}/>
                <Input placeholder="Pincode" value={form.address.pincode} onChange={e=>setForm(f=>({...f, address:{...f.address, pincode: e.target.value}}))}/>
                <Input placeholder="Country" value={form.address.country} onChange={e=>setForm(f=>({...f, address:{...f.address, country: e.target.value}}))}/>
              </div>
            </section>
            <section>
              <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">B2B GST (optional)</div>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Company name" value={form.buyer_company_name} onChange={e=>setForm(f=>({...f, buyer_company_name: e.target.value}))} data-testid="off-buyer-company"/>
                <Input placeholder="Buyer GSTIN (e.g. 29AAAAA0000A1Z5)" value={form.buyer_gstin} onChange={e=>setForm(f=>({...f, buyer_gstin: (e.target.value||"").toUpperCase()}))} data-testid="off-buyer-gstin"/>
              </div>
              <div className="text-[11px] text-stone-500 mt-1">Printed on the tax invoice so the buyer can claim input tax credit.</div>
            </section>
            <section>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-stone-500">Items</div>
                <button onClick={addLine} className="text-xs text-[#3B2F2F] hover:underline flex items-center gap-1" data-testid="off-add-line"><Plus className="w-3 h-3"/> Add row</button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-5" placeholder="Product name" value={it.product_name} onChange={e=>updLine(i, "product_name", e.target.value)} data-testid={`off-line-${i}-name`}/>
                    <Input className="col-span-2" placeholder="Weight (e.g. 250g)" value={it.variant_label} onChange={e=>updLine(i, "variant_label", e.target.value)} data-testid={`off-line-${i}-weight`}/>
                    <Input className="col-span-1" type="number" min="1" placeholder="Qty" value={it.qty} onChange={e=>updLine(i, "qty", Number(e.target.value))} data-testid={`off-line-${i}-qty`}/>
                    <Input className="col-span-3" type="number" min="0" placeholder="Unit price ₹" value={it.unit_price} onChange={e=>updLine(i, "unit_price", Number(e.target.value))} data-testid={`off-line-${i}-price`}/>
                    <button onClick={()=>rmLine(i)} disabled={form.items.length===1} className="col-span-1 text-red-500 disabled:text-stone-300 flex justify-center"><Trash2 className="w-4 h-4"/></button>
                  </div>
                ))}
              </div>
            </section>
            <section className="grid grid-cols-4 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-stone-500">Discount ₹</label>
                <Input type="number" min="0" value={form.discount} onChange={e=>setForm(f=>({...f, discount: Number(e.target.value)}))}/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-stone-500">Shipping ₹</label>
                <Input type="number" min="0" value={form.shipping} onChange={e=>setForm(f=>({...f, shipping: Number(e.target.value)}))}/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-stone-500">Tax ₹</label>
                <Input type="number" min="0" value={form.tax} onChange={e=>setForm(f=>({...f, tax: Number(e.target.value)}))}/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-stone-500">Payment</label>
                <Select value={form.payment_method} onValueChange={(v)=>setForm(f=>({...f, payment_method: v}))}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="bank">Bank transfer</SelectItem>
                    <SelectItem value="razorpay">Razorpay</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </section>
            <section>
              <label className="text-[10px] uppercase tracking-widest text-stone-500">Notes (optional)</label>
              <Input placeholder="e.g. delivered in person on Friday" value={form.notes} onChange={e=>setForm(f=>({...f, notes: e.target.value}))} data-testid="off-notes"/>
            </section>
            <section>
              <label className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5">
                <Truck className="w-3 h-3"/> Courier & AWB / Tracking number
              </label>
              <div className="flex gap-2 mt-1">
                <Select value={form.courier || DEFAULT_COURIER_KEY} onValueChange={v=>setForm(f=>({...f, courier: v}))}>
                  <SelectTrigger className="w-40" data-testid="off-courier"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {COURIERS.map(c => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input placeholder="AWB number" value={form.awb_number} onChange={e=>setForm(f=>({...f, awb_number: e.target.value}))} data-testid="off-awb" className="flex-1"/>
              </div>
              <div className="text-[11px] text-stone-500 mt-1">Pick the courier partner. The customer's tracking SMS uses the correct URL for that partner (Shipmaxx / Amazon / Envia / Ekart).</div>
            </section>
            <div className="flex justify-between items-center bg-[#FEF3E2]/50 border border-[#D4AF37]/30 rounded-lg p-3">
              <div className="text-xs text-stone-500">Subtotal: <span className="font-outfit font-semibold text-stone-800">{inr(subtotal)}</span></div>
              <div className="font-outfit font-bold text-2xl text-[#3B2F2F]">{inr(total)}</div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={closeModal} className="border border-stone-300 rounded-full px-5 py-2 text-sm hover:bg-stone-50">Cancel</button>
              <button onClick={submitForm} disabled={saving} data-testid="off-save"
                      className="bg-[#3B2F2F] text-white rounded-full px-6 py-2 text-sm font-medium hover:bg-black disabled:opacity-60">
                {saving ? "Saving…" : (mode === "edit" ? "Save changes" : "Save to ledger")}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4">
      <div className="text-xs uppercase tracking-widest text-stone-500">{label}</div>
      <div className="font-outfit font-bold text-2xl mt-1" style={{color: accent}}>{value}</div>
    </div>
  );
}

function Info({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className="text-sm text-stone-800 mt-0.5">{children}</div>
    </div>
  );
}

function AwbCell({ order, onSave }) {
  const [editing, setEditing] = useState(!order.awb_number);
  const [value, setValue] = useState(order.awb_number || "");
  const [courier, setCourier] = useState(order.courier || DEFAULT_COURIER_KEY);
  useEffect(() => {
    setValue(order.awb_number || "");
    setCourier(order.courier || DEFAULT_COURIER_KEY);
    setEditing(!order.awb_number);
  }, [order.awb_number, order.courier]);
  const commit = () => {
    const trimmed = value.trim();
    if (trimmed === (order.awb_number || "") && courier === (order.courier || DEFAULT_COURIER_KEY)) { setEditing(false); return; }
    onSave(order.order_id, order.order_no, trimmed, courier);
    setEditing(false);
  };
  if (!editing && order.awb_number) {
    return (
      <div className="flex flex-col gap-0.5 group">
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-xs text-stone-700 truncate max-w-[7.5rem]" title={order.awb_number} data-testid={`awb-value-${order.order_no}`}>
            {order.awb_number}
          </span>
          <button onClick={()=>setEditing(true)} data-testid={`awb-edit-${order.order_no}`}
                  className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-[#3B2F2F]" title="Edit AWB">
            <Pencil className="w-3 h-3"/>
          </button>
        </div>
        <span className="text-[10px] text-[#8B7355] uppercase tracking-widest" data-testid={`awb-courier-${order.order_no}`}>
          via {courierName(order.courier)}
        </span>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <select
        value={courier}
        onChange={(e)=>setCourier(e.target.value)}
        data-testid={`awb-courier-select-${order.order_no}`}
        className="w-full text-[11px] px-1.5 py-0.5 border border-stone-200 rounded focus:border-[#D4AF37] focus:outline-none bg-white"
      >
        {COURIERS.map(c => <option key={c.key} value={c.key}>{c.name}</option>)}
      </select>
      <input
        value={value}
        onChange={(e)=>setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e)=>{ if (e.key==="Enter") e.currentTarget.blur(); if (e.key==="Escape") { setValue(order.awb_number||""); setCourier(order.courier||DEFAULT_COURIER_KEY); setEditing(!order.awb_number); }}}
        placeholder="Add AWB…"
        data-testid={`awb-input-${order.order_no}`}
        className="w-full text-xs font-mono px-2 py-1 border border-stone-200 rounded focus:border-[#D4AF37] focus:outline-none"
      />
    </div>
  );
}
